/**
 * Storefront per-color image resolution -- product-image picker slice 1.
 *
 * Card: https://trello.com/c/efG02KCo
 * Design: docs/planning/2026-07-08-product-image-picker-design.md (Section 5).
 *
 * ``campaign_products.storefront_images`` is the SINGLE SOURCE OF TRUTH for
 * the builder's per-color image selection + order:
 *
 *     { "<color>": [{ source: "sanmar"|"artifi", url: "<resolved_url>",
 *                     position?: <int> }, ...] }
 *
 * Display order within a color is each entry's ``position`` (the global
 * cross-color order, see chosenInDisplayOrder) when present, else list
 * order; the first entry is the color's primary card image.
 * The shopper card, quickview, and cart all resolve images through the
 * helpers here so the fallback logic lives in exactly one place:
 *
 *   - When ``storefront_images[color]`` is a non-empty list, use it (the
 *     ordered, builder-chosen images -- already-resolved absolute URLs).
 *   - Otherwise FALL BACK to the legacy ``images[color]`` single uuid so
 *     every existing product that never used the picker renders exactly as
 *     before. (This is the fix for the "seeded fixture images unrelated to
 *     the real product" problem for products that DO use the picker, without
 *     touching products that don't.)
 *
 * These helpers return the RAW ref (uuid or resolved url); the caller runs
 * the value through ``getImage()`` (which now passes absolute URLs through
 * unchanged and prefixes bare uuids with the S3 base). Keeping the raw ref
 * here means the resolver has no dependency on the image-base config.
 */

/**
 * Return the ordered list of image refs for a color, preferring
 * ``storefront_images`` and falling back to the legacy single image.
 *
 * @param {object} campaignProduct - the CP (may be null/partial).
 * @param {string} color - the primary attribute value (color name).
 * @returns {string[]} ordered image refs (resolved URLs or legacy uuids).
 *   Empty array when neither source has an image for the color.
 */
export function resolveColorImageRefs (campaignProduct, color) {
  if (!campaignProduct) return []

  const picked = campaignProduct.storefront_images
  if (
    picked &&
    typeof picked === 'object' &&
    Array.isArray(picked[color]) &&
    picked[color].length > 0
  ) {
    // Usable entries only (a string url), in display order.
    return chosenInDisplayOrder({ [color]: picked[color] }).map(({ entry }) => entry.url)
  }

  const legacy = campaignProduct.images
  if (legacy && typeof legacy === 'object' && legacy[color]) {
    return [legacy[color]]
  }

  return []
}

/**
 * Return the single primary image ref for a color (the first of the
 * ordered list), or null when the color has no image from either source.
 *
 * @param {object} campaignProduct
 * @param {string} color
 * @returns {string|null}
 */
export function resolvePrimaryColorImageRef (campaignProduct, color) {
  const refs = resolveColorImageRefs(campaignProduct, color)
  return refs.length > 0 ? refs[0] : null
}

/**
 * GLOBAL display order across colors (Jake 2026-10-07, card
 * https://trello.com/c/Qlwx8mlT): the Chosen grid is ONE list a distributor
 * sorts across colors -- grouping it by color made "Navy first, then a Red,
 * then Navy again" impossible.
 *
 * Storage stays keyed by color (the PDP's per-color gallery reads it). Each
 * entry carries a ``position``: an integer, contiguous 0..n-1 across the
 * WHOLE map, giving the global order. Every writer here re-stamps positions
 * and keeps each color's list sorted by position, so "list order is display
 * order" still holds WITHIN a color and every per-color reader is unchanged.
 *
 * Existing data has no ``position``. Its order is the one the grid always
 * showed: selected-color order, then within-color list order (then any other
 * stored color, in key order). An entry WITHOUT a position in a map that
 * has them sorts after every positioned entry -- a newly appended image goes
 * to the end. Positions are written on the first editor write; there is no
 * migration.
 */

function isUsableEntry (entry) {
  return Boolean(entry) && typeof entry === 'object' &&
    typeof entry.url === 'string' && entry.url.length > 0
}

function hasPosition (entry) {
  return isUsableEntry(entry) && Number.isInteger(entry.position) && entry.position >= 0
}

function mapHasPositions (map) {
  return Object.values(map).some((list) => Array.isArray(list) && list.some(hasPosition))
}

/**
 * True when any chosen image carries a ``position`` -- i.e. the map has a
 * global display order (written by the editor since Jake 2026-10-07).
 *
 * @param {object} map - the storefront_images map (may be null).
 * @returns {boolean}
 */
export function hasGlobalOrder (map) {
  return Boolean(map) && typeof map === 'object' && mapHasPositions(map)
}

/**
 * The chosen images in GLOBAL display order.
 *
 * @param {object} map - the storefront_images map (may be null).
 * @param {string[]} [colorOrder] - color NAMES in selected order; ranks
 *   position-less entries (the default order of existing data).
 * @returns {Array<{color: string, entry: object, withinColorIndex: number}>}
 *   usable entries only; ``withinColorIndex`` is the entry's index in its
 *   stored color list.
 */
export function chosenInDisplayOrder (map, colorOrder = []) {
  const current = map && typeof map === 'object' ? map : {}
  const ranked = [...new Set(colorOrder)]
  const colors = [
    ...ranked.filter((c) => Array.isArray(current[c])),
    ...Object.keys(current).filter((c) => !ranked.includes(c)),
  ]
  const items = []
  for (const color of colors) {
    const list = current[color]
    if (!Array.isArray(list)) continue
    list.forEach((entry, withinColorIndex) => {
      if (isUsableEntry(entry)) items.push({ color, entry, withinColorIndex })
    })
  }
  const rank = (item) => (hasPosition(item.entry) ? item.entry.position : Infinity)
  // Array.prototype.sort is stable, so ties keep the default order.
  return items
    .map((item, defaultIndex) => ({ item, defaultIndex }))
    .sort((a, b) => rank(a.item) - rank(b.item) || a.defaultIndex - b.defaultIndex)
    .map(({ item }) => item)
}

// Write ``ordered`` (a chosenInDisplayOrder-shaped list) back as a map:
// positions 0..n-1 in that order, each color's list sorted by position.
// Every key of ``map`` survives -- an empty list means "curated, nothing
// chosen" (#4071) and must not be dropped. A malformed entry (the validator
// rejects these, so none should exist) is kept at the end of its color.
function withOrder (map, ordered) {
  const next = {}
  for (const color of Object.keys(map)) {
    next[color] = []
  }
  ordered.forEach(({ color, entry }, position) => {
    if (!next[color]) next[color] = []
    next[color].push({ ...entry, position })
  })
  for (const [color, list] of Object.entries(map)) {
    if (!Array.isArray(list)) {
      next[color] = list
      continue
    }
    next[color].push(...list.filter((entry) => !isUsableEntry(entry)))
  }
  return next
}

/**
 * Re-stamp positions contiguously in the current global order.
 *
 * Returns the SAME map when it has no positions and no ``colorOrder`` is
 * given: without the selected-color order the default order of existing
 * data is unknown, so it is left for the editor to normalize.
 *
 * @param {object} map
 * @param {string[]} [colorOrder]
 * @returns {object}
 */
export function renumberPositions (map, colorOrder = []) {
  const current = map && typeof map === 'object' ? map : {}
  if (colorOrder.length === 0 && !mapHasPositions(current)) return current
  return withOrder(current, chosenInDisplayOrder(current, colorOrder))
}

/**
 * Move one chosen image anywhere in the global order -- across colors too.
 *
 * Splice semantics matching sortablejs ``onEnd`` over the flat Chosen grid:
 * the image at ``fromIdx`` is removed and re-inserted at ``toIdx``. The image
 * keeps its color; only positions change. A no-op or a bad index returns the
 * SAME map so the caller can skip a redundant dirty write.
 *
 * @param {object} map
 * @param {string[]} colorOrder
 * @param {number} fromIdx
 * @param {number} toIdx
 * @returns {object}
 */
export function moveChosenImage (map, colorOrder, fromIdx, toIdx) {
  const current = map && typeof map === 'object' ? map : {}
  const ordered = chosenInDisplayOrder(current, colorOrder)
  const inRange = (i) => Number.isInteger(i) && i >= 0 && i < ordered.length
  if (!inRange(fromIdx) || !inRange(toIdx) || fromIdx === toIdx) return current
  const [moved] = ordered.splice(fromIdx, 1)
  ordered.splice(toIdx, 0, moved)
  return withOrder(current, ordered)
}

/**
 * Undo a ``withoutColors`` prune: put the removed colors back, each image
 * in the global slot it held before.
 *
 * ``removed`` is ``{color: entries}`` exactly as they were stored. Each
 * restored image is re-inserted at its old position, in ascending order, so
 * with no edits in between the result is the map before the prune.
 * Position-less removed data is merged back as-is (after any positioned
 * images).
 *
 * @param {object} map
 * @param {object} removed
 * @param {string[]} [colorOrder]
 * @returns {object}
 */
export function restoreColors (map, removed, colorOrder = []) {
  const current = map && typeof map === 'object' ? map : {}
  const back = removed && typeof removed === 'object' ? removed : {}
  const merged = { ...current, ...back }
  // Nothing to put back in a slot: merge, renumbering only a positioned map.
  if (!mapHasPositions(back)) return renumberPositions(merged)

  const restoring = new Set(Object.keys(back))
  const ordered = chosenInDisplayOrder(current, colorOrder)
    .filter((item) => !restoring.has(item.color))
  const returning = chosenInDisplayOrder(back, colorOrder)
  for (const item of returning) {
    const slot = hasPosition(item.entry) ? item.entry.position : ordered.length
    ordered.splice(Math.min(slot, ordered.length), 0, item)
  }
  return withOrder(merged, ordered)
}

/**
 * Default-include a color's decorated render (Jake 2026-10-06, card
 * https://trello.com/c/Qlwx8mlT): every selected color gets a render and the
 * render is CHOSEN by default -- but a distributor's removal must stick.
 *
 * KEY PRESENCE is the durable "has this color been curated" signal, and it
 * needs no new field: an ABSENT ``storefront_images[color]`` key means the
 * color was never curated (the shopper falls back to the legacy image), so
 * the render is added as the color's only entry. A PRESENT key -- even ``[]``
 * -- means the distributor has shaped this color's list (removing the render
 * leaves the key behind), so it is left alone. Deselecting a color deletes
 * its key (``withoutColors``), which is what makes a re-selected color start
 * fresh.
 *
 * ``replacesUrl`` is the REGENERATION path: the color's render changed in
 * this session because its artwork changed. The new render REPLACES the stale
 * one IN PLACE -- and only when the stale render is actually in Chosen. If it
 * was only in the pool (a curated color) or the distributor removed it, the
 * map is returned unchanged and the new render stays in the pool: Jake's
 * rules are "respect removal" and "curated colors are pool-only" (#4071
 * gate, 2026-10-06; an earlier "regenerate re-includes at the front" was an
 * inference, not his rule).
 *
 * GLOBAL order: a newly included render goes to the END of the Chosen grid
 * (the distributor can drag it anywhere); a regenerated render takes the
 * stale one's slot. Positions stay contiguous. On position-less data nothing
 * is stamped -- the editor normalizes on its next write.
 *
 * Returns a NEW map, or the SAME map on a no-op so the caller can skip a
 * redundant dirty write.
 *
 * @param {object} map - the current storefront_images map (may be null).
 * @param {string} color - the color NAME (the map key).
 * @param {string} url - the render's resolved url.
 * @param {{replacesUrl?: string|null}} [opts]
 * @returns {object}
 */
export function includeColorRender (map, color, url, { replacesUrl = null } = {}) {
  const current = map && typeof map === 'object' ? map : {}
  if (!color || typeof url !== 'string' || url.length === 0) return current
  const entry = { source: 'artifi', url, color }
  const hasKey = Object.prototype.hasOwnProperty.call(current, color)

  if (!hasKey) {
    return renumberPositions({ ...current, [color]: [entry] })
  }
  if (!replacesUrl || replacesUrl === url) return current

  const list = Array.isArray(current[color]) ? current[color] : []
  const isUrl = (e, u) => e && typeof e === 'object' && e.url === u
  const staleIdx = list.findIndex((e) => isUrl(e, replacesUrl))
  if (staleIdx < 0) return current // stale render not chosen -> pool only
  if (list.some((e) => isUrl(e, url))) {
    // The new render is already chosen; just drop the stale one.
    return renumberPositions({ ...current, [color]: list.filter((_, i) => i !== staleIdx) })
  }
  const nextList = [...list]
  const stale = list[staleIdx]
  nextList[staleIdx] = hasPosition(stale) ? { ...entry, position: stale.position } : entry
  return { ...current, [color]: nextList }
}

/**
 * Drop whole colors from ``storefront_images`` -- render AND blank images
 * (Jake 2026-10-06: deselecting a color removes it from the storefront
 * listing). Deleting the KEY, not emptying it, is deliberate: an absent key
 * is "never curated", so re-selecting the color later starts fresh (see
 * ``includeColorRender``).
 *
 * The images left behind are renumbered so positions stay contiguous;
 * ``restoreColors`` undoes this.
 *
 * Returns the SAME map when none of the named colors is present.
 *
 * @param {object} map - the current storefront_images map (may be null).
 * @param {string[]} colors - color NAMES to drop.
 * @returns {object}
 */
export function withoutColors (map, colors) {
  const current = map && typeof map === 'object' ? map : {}
  const drop = (colors ?? []).filter((c) =>
    Object.prototype.hasOwnProperty.call(current, c)
  )
  if (drop.length === 0) return current
  const next = { ...current }
  for (const c of drop) delete next[c]
  return renumberPositions(next)
}

/**
 * Drop every decorated render (``source: 'artifi'``) from
 * ``storefront_images`` -- what the server does when a placement is removed
 * (GH #4286 / #4287): a render depicts placements the product no longer has.
 * A color emptied BY THIS loses its key (never curated, so the next render is
 * default-chosen again); a color already ``[]`` keeps it. Positions are left
 * as they are, so the result equals the server's map exactly.
 *
 * Mirrors ``without_listing_renders`` in
 * api/src/services/editor_render_pointers.py; keep the two rules identical.
 *
 * Returns the SAME map when there is no render to drop.
 *
 * @param {object} map - the storefront_images map (may be null).
 * @returns {object}
 */
export function withoutRenderImages (map) {
  const current = map && typeof map === 'object' ? map : {}
  const isRender = (e) => Boolean(e) && typeof e === 'object' && e.source === 'artifi'
  let changed = false
  const next = {}
  for (const [color, list] of Object.entries(current)) {
    if (!Array.isArray(list) || !list.some(isRender)) {
      next[color] = list
      continue
    }
    changed = true
    const kept = list.filter((e) => !isRender(e))
    if (kept.length > 0) next[color] = kept
  }
  return changed ? next : current
}

/**
 * Resolve selected color SKUS to the color NAMES that key
 * ``storefront_images``. A catalog row's ``name`` can differ from its
 * ``color_sku``; a sku the catalog does not know falls back to itself.
 *
 * @param {string[]} skus - selected color skus.
 * @param {Array<{color_sku: string, name?: string}>} catalogColors - the
 *   supplier catalog rows (``supplierMeta.colors``); may be null.
 * @returns {string[]}
 */
export function colorNamesForSkus (skus, catalogColors) {
  const bySku = new Map(
    (Array.isArray(catalogColors) ? catalogColors : []).map((c) => [c?.color_sku, c])
  )
  return (Array.isArray(skus) ? skus : []).map((sku) => bySku.get(sku)?.name ?? sku)
}

// ---------------------------------------------------------------------------
// The distributor's OWN preview image for a color (card #243). The server
// writes it (POST /api/campaign_products/<id>/color_previews) as a
// ``source: 'upload'`` entry FIRST in that color's list; these mirror
// api/src/services/storefront_image_order.py so the editor can apply the same
// edit to a pending listing value. Keep the two rules identical
// (storefrontImagesColorUpload.spec.js mirrors test_storefront_image_order.py).
// ---------------------------------------------------------------------------

function isUploadEntry (entry) {
  return Boolean(entry) && typeof entry === 'object' && entry.source === 'upload'
}

/**
 * The url of ``color``'s uploaded preview image, or null.
 *
 * @param {object} map - the storefront_images map (may be null).
 * @param {string} color - the color NAME (the map key).
 * @returns {string|null}
 */
export function colorUploadUrl (map, color) {
  const list = map && typeof map === 'object' ? map[color] : null
  if (!Array.isArray(list)) return null
  const entry = list.find((e) => isUploadEntry(e) && isUsableEntry(e))
  return entry ? entry.url : null
}

/**
 * ``map`` with ``entry`` as ``color``'s FIRST image: an earlier upload for the
 * color is replaced, its other images stay after it. In a positioned map the
 * upload takes the color's current lead slot (or the end, for a color with no
 * images) and later entries shift by one.
 *
 * @param {object} map
 * @param {string} color
 * @param {object} entry - ``{source: 'upload', url, key, color}``
 * @returns {object} a new map
 */
export function withColorUpload (map, color, entry) {
  const current = map && typeof map === 'object' ? map : {}
  if (!mapHasPositions(current)) {
    const kept = (Array.isArray(current[color]) ? current[color] : [])
      .filter((e) => !isUploadEntry(e))
    return { ...current, [color]: [{ ...entry }, ...kept] }
  }
  const base = Object.prototype.hasOwnProperty.call(current, color)
    ? current
    : { ...current, [color]: [] }
  const ordered = chosenInDisplayOrder(base)
    .filter((item) => !(item.color === color && isUploadEntry(item.entry)))
  const lead = ordered.findIndex((item) => item.color === color)
  ordered.splice(lead < 0 ? ordered.length : lead, 0, { color, entry: { ...entry } })
  const stripped = {
    ...base,
    [color]: (Array.isArray(base[color]) ? base[color] : []).filter((e) => !isUploadEntry(e)),
  }
  return withOrder(stripped, ordered)
}

/**
 * ``map`` without ``color``'s uploaded images. A color this empties loses its
 * key; positions close up. Returns the SAME map when the color is absent.
 *
 * @param {object} map
 * @param {string} color
 * @returns {object}
 */
export function withoutColorUploads (map, color) {
  const current = map && typeof map === 'object' ? map : {}
  if (!Object.prototype.hasOwnProperty.call(current, color)) return current
  const list = Array.isArray(current[color]) ? current[color] : []
  const kept = list.filter((e) => !isUploadEntry(e))
  let next
  if (!mapHasPositions(current)) {
    next = { ...current, [color]: kept }
  } else {
    const ordered = chosenInDisplayOrder(current)
      .filter((item) => !(item.color === color && isUploadEntry(item.entry)))
    next = withOrder({ ...current, [color]: kept }, ordered)
  }
  if (Array.isArray(next[color]) && next[color].length === 0) delete next[color]
  return next
}
